/**
 * Retrieves all tabbable elements within a given element.
 * 
 * @param element - The root element to search within.
 * @returns A NodeList of all tabbable elements within the given element.
 */
export const getTabbableElements = (element: HTMLElement): NodeListOf<HTMLElement> => {
  return element.querySelectorAll(
    "a[href]:not([tabindex='-1'])," +
    "area[href]:not([tabindex='-1'])," +
    "button:not([disabled]):not([tabindex='-1'])," +
    "input:not([disabled]):not([tabindex='-1']):not([type='hidden'])," +
    "select:not([disabled]):not([tabindex='-1'])," +
    "textarea:not([disabled]):not([tabindex='-1'])," +
    "iframe:not([tabindex='-1'])," +
    "details:not([tabindex='-1'])," +
    "[tabindex]:not([tabindex='-1'])," +
    "[contentEditable=true]:not([tabindex='-1'])"
  );
};

/**
 * Serializes a parameter based on a given specification.
 * Forked from https://github.com/RemiBou/BrowserInterop
 * 
 * @param data - The data to serialize.
 * @param spec - The specification to use for serialization.
 * @returns The serialized data.
 */
export const serializeParameter = (data: any, spec?: any): any => {
  if (typeof data === "undefined" || data === null) {
    return null;
  }
  if (typeof data === "number" || typeof data === "string" || typeof data === "boolean") {
    return data;
  }

  let res: any = Array.isArray(data) ? [] : {};
  if (!spec) {
    spec = "*";
  }

  for (let i in data) {
    if (data.hasOwnProperty(i)) {
      let currentMember = data[i];

      // Skip functions and null values
      if (typeof currentMember === 'function' || currentMember === null) {
        continue;
      }

      let currentMemberSpec;
      if (spec !== "*") {
        currentMemberSpec = Array.isArray(data) ? spec : spec[i];
        if (!currentMemberSpec) {
          continue;
        }
      } else {
        currentMemberSpec = "*";
      }

      if (typeof currentMember === 'object') {
        if (Array.isArray(currentMember) || currentMember.length) {
          res[i] = [];
          for (let j = 0; j < currentMember.length; j++) {
            const arrayItem = currentMember[j];
            if (typeof arrayItem === 'object') {
              res[i].push(serializeParameter(arrayItem, currentMemberSpec));
            } else {
              res[i].push(arrayItem);
            }
          }
        } else {
          // The browser provides some members (like plugins) as hash with index as key, if length == 0 we shall not convert it
          if (currentMember.length === 0) {
            res[i] = [];
          } else {
            res[i] = serializeParameter(currentMember, currentMemberSpec);
          }
        }
      } else {
        // Handle string, number, or boolean
        if (currentMember === Infinity) { // Infinity is not serialized by JSON.stringify
          currentMember = "Infinity";
        }
        if (currentMember !== null) { // Needed because the default JSON serializer in jsinterop serializes null values
          res[i] = currentMember;
        }
      }
    }
  }

  return res;
};

/**
  * Global function to invoke a method asynchronously.
  * @param {string} callbackEventName
  * @param {any} dotNetHelper
  */
export const invokeMethodAsync = (callbackEventName: string, dotNetHelper: any) => {
  dotNetHelper.invokeMethodAsync(callbackEventName);
};

/**
  * Global function to check if an input has invalid characters.
  * @param {any} input The input value.
  * @param {any} validChars The valid characters.
  * @returns {boolean} True if the input has invalid characters; otherwise, false.
  */
export const hasInvalidChars = (input: string, validChars: string) => {
  if (input.length <= 0 || validChars.length <= 0)
    return false;

  let inputCharArr = input.split('');
  for (let i = 0; i < inputCharArr.length; i++) {
    if (!validChars.includes(inputCharArr[i]))
      return true;
  }

  return false;
};

// move to scroll helper

/**
  * Global function to scroll to the bottom of an element.
  * @param {HTMLElement | string} elementOrSelector The element, element id, or selector.
  */
export const scrollToElementBottom = (elementOrSelector: HTMLElement | string) => {
  let el: HTMLElement | null;

  if (typeof elementOrSelector === 'string') {
    el = document.querySelector(elementOrSelector);
  } else {
    el = elementOrSelector;
  }

  if (el) {
    el.scrollTop = el.scrollHeight;
  } else {
    console.error('Element not found for selector:', elementOrSelector);
  }
};

/**
  * Global function to scroll to the top of an element.
  * @param {HTMLElement | string} elementOrSelector The element, element id, or selector.
  */
export const scrollToElementTop = (elementOrSelector: HTMLElement | string) => {
  let el: HTMLElement | null;

  if (typeof elementOrSelector === 'string') {
    el = document.querySelector(elementOrSelector);
  } else {
    el = elementOrSelector;
  }

  if (el) {
    el.scrollTop = 0;
  } else {
    console.error('Element not found for selector:', elementOrSelector);
  }
};